import * as React from "react"
import { Link } from "react-router-dom"
import {
  type AgingSummary,
  Flow,
  PartyDesignation,
  ReportKind,
} from "@digits-graphql/frontend/graphql-bearer"
import { buildZeroMoneyFlow } from "@digits-shared/helpers/moneyFlowHelper"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"
import { useIsSectionCollapsed } from "src/frontend/components/OS/Applications/Reports/Report/Options/CollapsingSections"
import {
  AGING_COLUMN_OFFSET,
  columnsByComponentSize,
} from "src/frontend/components/Shared/Layout/Components/Statements/AgingReport/AgingColumns"
import { CollapsibleRow } from "src/frontend/components/Shared/Layout/Components/Statements/CollapsibleRow"
import {
  useStatementComponentSize,
  useStatementKind,
} from "src/frontend/components/Shared/Layout/Components/Statements/StatementContext"
import {
  Amount,
  ParentRow,
  ParentTitle,
} from "src/frontend/components/Shared/Layout/Components/Statements/StatementRowShared"
import { type RowDetails } from "src/frontend/components/Shared/Layout/Components/Statements/toDetailsData"
import { useRowDetailsLinks } from "src/frontend/components/Shared/Layout/Components/Statements/useRowDetailsLinks"

export interface ParentAgingSummaryProps {
  rowId: string
  depth: number
  summary: AgingSummary
  details?: RowDetails
}

const StyledParentRow = styled(ParentRow)`
  padding-right: 0;
  padding-top: 4px;
  border-bottom: 1px solid ${colors.secondary20};
`

const StyledRow = styled(ParentRow)<{ $collapsed: boolean; $columnCount: number }>`
  padding-right: 0;
  padding-top: 4px;
  border-bottom: 1px solid ${colors.secondary20};
  grid-template-columns: repeat(${(props) => props.$columnCount}, minmax(0, 1fr));
  ${(props) =>
    props.$collapsed
      ? css`
          border-bottom: 1px solid ${colors.secondary20};
          margin-bottom: 2px;
        `
      : css`
          font-weight: ${fonts.weight.heavy};
          margin-bottom: 5px;
        `}
`

const StyledCollapsibleRow = styled(CollapsibleRow)`
  font-weight: ${fonts.weight.heavy};
`

export const ParentAgingSummaryComponent: React.FC<ParentAgingSummaryProps> = ({
  rowId,
  depth,
  summary: { label, dimensionSummary },
  details,
}) => {
  const kind = useStatementKind()
  const isSectionCollapsed = useIsSectionCollapsed(rowId)
  const { disabled, path } = useRowDetailsLinks(details)
  const componentSize = useStatementComponentSize()

  const title = React.useMemo(
    () => (isSectionCollapsed ? label.replace("Total ", "") : label),
    [isSectionCollapsed, label]
  )

  const columns = columnsByComponentSize(componentSize)

  let contents: React.ReactNode = null

  // We don't query for the hover details
  const hoverDetails = {
    periodDetails: {
      hover: {
        entity: {
          id: rowId.replace("-summary", ""),
          designation: PartyDesignation.BusinessDesignation,
          name: label.replace("Total ", ""),
        },
        history: dimensionSummary,
      },
    },
  }

  if (componentSize.isExtraLarge || componentSize.isLarge) {
    contents = (
      <StyledParentRow
        id={rowId}
        depth={depth}
        details={hoverDetails}
        $collapsed={isSectionCollapsed}
      >
        <ParentTitle
          $collapsed={isSectionCollapsed}
          disabled={disabled}
          depth={depth}
          css={`
            grid-column: span ${AGING_COLUMN_OFFSET};
          `}
        >
          <Link to={path}>{title}</Link>
        </ParentTitle>
        {dimensionSummary.aging.map((agingDimension, i) => (
          <Amount
            rowId={rowId}
            key={`${rowId}-${i}`}
            moneyFlow={
              agingDimension.summary.total.current?.total ??
              buildZeroMoneyFlow(kind === ReportKind.APAging ? Flow.Outbound : Flow.Inbound)
            }
          />
        ))}
        <Amount
          rowId={rowId}
          moneyFlow={
            dimensionSummary.summary.total.current?.total ??
            buildZeroMoneyFlow(kind === ReportKind.APAging ? Flow.Outbound : Flow.Inbound)
          }
        />
      </StyledParentRow>
    )
  } else if (componentSize.isMedium) {
    contents = (
      <StyledRow
        id={rowId}
        depth={depth}
        details={hoverDetails}
        css=""
        $collapsed={isSectionCollapsed}
        $columnCount={columns.length}
      >
        <ParentTitle
          $collapsed={isSectionCollapsed}
          disabled={disabled}
          depth={depth}
          css={`
            grid-column: span ${columns.length - 1};
          `}
        >
          <Link to={path}>{title}</Link>
        </ParentTitle>
        <Amount
          rowId={rowId}
          moneyFlow={
            dimensionSummary.summary.total.current?.total ??
            buildZeroMoneyFlow(kind === ReportKind.APAging ? Flow.Outbound : Flow.Inbound)
          }
        />
      </StyledRow>
    )
  }

  return isSectionCollapsed ? (
    <StyledCollapsibleRow rowId={rowId} depth={depth}>
      {contents}
    </StyledCollapsibleRow>
  ) : (
    contents
  )
}
